/**
 * A code-split route that can be RESOLVED BEFORE the first render.
 *
 * Why this exists instead of `lazy()` + `<Suspense>` (the pattern the twenty
 * signed-in routes in `app.tsx` use):
 *
 * The public routes are PRERENDERED. Their HTML already contains the finished
 * page, and `entry-client.tsx` calls `hydrateRoot` over it — so the client's
 * FIRST render has to produce exactly that markup. Two things make `React.lazy`
 * unable to do that here:
 *
 *  1. `lazy()` suspends on its first render even when the module is already in
 *     the browser's module cache: the initializer stores the thenable and only
 *     resolves on a microtask, so there is no synchronous path through it.
 *  2. `renderToString` (entry-server) wraps nothing in `<Suspense>`, so the
 *     prerendered HTML carries no `<!--$-->`/`<!--/$-->` boundary markers. A
 *     `<Suspense>` that appears only on the client fails to claim a hydratable
 *     suspense instance → hydration mismatch → React throws the prerendered
 *     article away and re-renders the whole subtree on the client, which is the
 *     opposite of what prerendering is for.
 *
 * So the module is loaded BEFORE React mounts (`preload()`, called from
 * entry-client for the path being hydrated) and the returned component renders
 * it SYNCHRONOUSLY from then on — no Suspense, no boundary markers, no
 * mismatch. On a client-side navigation to the same route nothing was
 * preloaded, and the component renders the same `<PageSpinner />` the
 * `<Suspense fallback>` would have shown while the chunk arrives.
 *
 * The loader goes through {@link lazyRoute}, so a chunk pruned by a deploy
 * still gets the one-reload recovery (and the `chunk_reload_recovered` event)
 * every other split route gets — see `lib/lazy-route.ts`.
 */
import { useSyncExternalStore, type ReactElement } from 'react';
import { PageSpinner } from '../components/ui/spinner';
import { lazyRoute } from './lazy-route';

/** A route component as the router mounts it: no props, ever. */
type RouteComponent = () => ReactElement;

export type PreloadableRoute = RouteComponent & {
  /** Load the chunk. Idempotent, never rejects — a failure surfaces as a
   *  render-time throw on the route instead, exactly like `React.lazy`. */
  preload: () => Promise<void>;
};

export function preloadableRoute(
  /** Route name for the stale-chunk telemetry in `lazyRoute`. */
  route: string,
  load: () => Promise<{ default: RouteComponent }>,
): PreloadableRoute {
  let Loaded: RouteComponent | null = null;
  let failure: unknown = null;
  let pending: Promise<void> | null = null;
  /** Bumped once the chunk settles; the only thing the store exposes. */
  let version = 0;
  const listeners = new Set<() => void>();
  const fetchChunk = lazyRoute(route, load);

  function settled(): void {
    version += 1;
    for (const notify of listeners) notify();
  }

  function start(): Promise<void> {
    pending ??= fetchChunk().then(
      (mod) => {
        Loaded = mod.default;
        settled();
      },
      (err: unknown) => {
        // Kept, not re-thrown here: an unhandled rejection would leave the
        // route spinning forever. It is re-thrown during render below, where
        // the route's `errorElement` can catch it.
        failure = err;
        settled();
      },
    );
    return pending;
  }

  function subscribe(notify: () => void): () => void {
    listeners.add(notify);
    return () => {
      listeners.delete(notify);
    };
  }

  const snapshot = (): number => version;

  function Route(): ReactElement {
    // Re-render this route (and only this route) when the chunk lands. Not
    // `useState` + `useEffect`: the chunk can settle between render and
    // subscribe, and `useSyncExternalStore` is the hook that closes that race.
    useSyncExternalStore(subscribe, snapshot, snapshot);
    if (failure !== null) throw failure;
    if (Loaded === null) {
      // Kick the load off during render, like `React.lazy` does — waiting for
      // an effect would cost a paint before the request even starts. Idempotent.
      void start();
      return <PageSpinner />;
    }
    return <Loaded />;
  }
  Route.displayName = `PreloadableRoute(${route})`;
  Route.preload = start;

  return Route;
}
